/* eslint-disable max-len */
/**
 * @file src/utils/httpClient http请求封装
 * @author sunyingying01
 */

import ky, {type Options as KyOptions} from 'ky';
import {v4 as uuidv4} from 'uuid';
import {responseUnpackHook, commonHeaderHook} from './ky-hooks';

/** TODO 还要修改 */
// 创建基础实例
function getPrefixUrl(): string {
    const hostname = window.location.hostname;
    const isDev = hostname === 'localhost' || hostname.includes('-dev.');

    if (isDev) {
        return '/proxyApi';
    }

    // return 'https://pro-service.famou.com';
    return '/proxyApi';
}
const httpClient = ky.create({
    prefixUrl: getPrefixUrl(),
    timeout: 60000,
    credentials: 'include',
    hooks: {
        beforeRequest: [commonHeaderHook],
        afterResponse: [responseUnpackHook]
    }
});

// 扩展请求选项类型
interface RequestOptions {
    headers?: Record<string, string>;
    signal?: AbortSignal;
    timeout?: number | false;
}

// 封装常用方法
export const requestApi = {
    /**
     * GET 请求（支持取消）
     */
    get: (
        url: string,
        params?: Record<string, any>,
        options?: RequestOptions
    ): Promise<globalThis.ApiResponse> => {
        const {headers, signal} = options || {};

        return httpClient.get(url, {
            searchParams: params,
            headers: {
                ...headers
            },
            signal
        }).json();
    },

    /**
     * POST 请求（支持取消）
     */
    post: (
        url: string,
        body: any,
        options?: RequestOptions
    ): Promise<globalThis.ApiResponse> => {
        const {headers, signal, timeout} = options || {};

        const kyOptions: KyOptions = {
            headers: {
                ...headers
            },
            signal,
            ...(timeout !== undefined && {timeout})
        };

        if (body instanceof FormData) {
            kyOptions.body = body;
            // 删除Content-Type让浏览器自动设置boundary
            if (kyOptions.headers) {
                delete (kyOptions.headers as any)['Content-Type'];
            }
        } else {
            kyOptions.json = body;
        }

        return httpClient.post(url, kyOptions).json();
    },

    /**
     * PUT 请求（支持取消）
     */
    put: (
        url: string,
        body: Record<string, any>,
        options?: RequestOptions
    ): Promise<globalThis.ApiResponse> => {
        const {headers, signal} = options || {};

        return httpClient.put(url, {
            json: body,
            headers: {
                ...headers
            },
            signal
        }).json();
    },

    /**
     * DELETE 请求（支持取消）
     */
    delete: (
        url: string,
        params?: Record<string, any>,
        options?: RequestOptions
    ): Promise<globalThis.ApiResponse> => {
        const {headers, signal} = options || {};

        return httpClient.delete(url, {
            json: params,
            headers: {
                ...headers
            },
            signal
        }).json();
    },

    /**
     * 文件上传（支持取消）
     */
    upload: (
        url: string,
        file: File,
        options?: RequestOptions
    ): Promise<globalThis.ApiResponse> => {
        const {headers, signal} = options || {};

        const formData = new FormData();
        formData.append('file', file);

        return httpClient.post(url, {
            body: formData,
            headers: {
                ...headers
            },
            signal
        }).json();
    },

    /**
     * 流式请求（支持取消）
     */
    getStream: async (
        url: string,
        params?: Record<string, any>,
        options?: RequestOptions & {extraOptions?: Record<string, any>}
    ): Promise<Response> => {
        const {headers, signal, extraOptions} = options || {};

        const queryString = new URLSearchParams(params).toString();
        const response = await fetch(`${getPrefixUrl()}/${url}?${queryString}`, {
            method: 'GET',
            headers: {
                ...headers,
                'x-bce-request-id': uuidv4(),
                Accept: 'text/event-stream' // SSE 一般需要这个
            },
            signal,
            // credentials: 'include',
            ...extraOptions // 允许传入额外的配置
        });

        if (!response.body) {
            throw new Error('ReadableStream not supported in this environment');
        }

        return response;
    }
};

export default httpClient;
